import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Scale, ChevronDown, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@core/lib/utils";
import { useNotifications } from "@core/hooks/useNotifications";
import NotificationBadge from "@core/components/notifications/NotificationBadge";
import NotificationPanel from "@core/components/notifications/NotificationPanel";

interface TopNavProps {
  onMenuOpen?: () => void;
  className?: string;
}

export const TopNav: React.FC<TopNavProps> = ({ onMenuOpen, className }) => {
  const location = useLocation();
  const [isLegalDropdownOpen, setIsLegalDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hook des notifications
  const {
    isOpen: isNotificationPanelOpen,
    badgeState,
    creditInfo,
    notifications,
    encouragementMessage,
    togglePanel: toggleNotificationPanel,
    closePanel: closeNotificationPanel,
    dismissNotification,
  } = useNotifications();

  const { t } = useTranslation("polls");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsLegalDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={cn(
        "h-14 bg-card border-b border-border flex items-center justify-between px-4",
        className,
      )}
    >
      {/* Logo DooDates à gauche + bouton menu */}
      <div className="flex items-center gap-3">
        {/* Bouton menu - visible uniquement sur mobile */}
        {onMenuOpen && (
          <button
            onClick={onMenuOpen}
            className="md:hidden p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={t("topnav.open_menu")}
            data-testid="topnav-button"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Logo DooDates */}
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">D</span>
          </div>
          <span className="text-foreground font-semibold">DooDates</span>
        </Link>
      </div>

      {/* Zone droite avec icônes */}
      <div className="flex items-center gap-2">
        {/* Icône notification avec badge */}
        <NotificationBadge
          badgeState={badgeState}
          onClick={toggleNotificationPanel}
        />

        {/* Menu légal */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsLegalDropdownOpen(!isLegalDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
            aria-label={t("topnav.legal_menu")}
          >
            <Scale className="w-4 h-4" />
            <span className="hidden sm:inline text-sm">
              {t("topnav.legal")}
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>

          {isLegalDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50">
              <Link
                to="/about"
                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => setIsLegalDropdownOpen(false)}
              >
                <FileText className="w-4 h-4" />À propos
              </Link>
              <Link
                to="/docs/cg-u"
                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => setIsLegalDropdownOpen(false)}
              >
                <FileText className="w-4 h-4" />
                {t("topnav.terms_of_use")}
              </Link>
              <Link
                to="/docs/cg-v"
                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => setIsLegalDropdownOpen(false)}
              >
                <FileText className="w-4 h-4" />
                {t("topnav.terms_of_sale")}
              </Link>
              <Link
                to="/docs/privacy"
                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => setIsLegalDropdownOpen(false)}
              >
                <FileText className="w-4 h-4" />
                {t("topnav.privacy")}
              </Link>
              <Link
                to="/docs/mentions-legales"
                className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => setIsLegalDropdownOpen(false)}
              >
                <FileText className="w-4 h-4" />
                {t("topnav.legal_notice")}
              </Link>
            </div>
          )}
        </div>

        {/* Espace pour équilibre mobile */}
        <div className="w-9 md:hidden" />
      </div>

      {/* Panneau de notifications */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={closeNotificationPanel}
        creditInfo={creditInfo}
        notifications={notifications}
        encouragementMessage={encouragementMessage}
        onDismiss={dismissNotification}
      />
    </nav>
  );
};
